/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Table,
  RowData,
  flexRender,
  Header,
  CoreRow
} from '@tanstack/react-table';
import { Fragment, useMemo, useRef } from 'react';
import { useMeasure } from 'react-use';
import { TableSortAscButton, TableSortDescButton } from './tableSortButtons';
import { StickyOptions } from './tableTypes';
import TableFilterToggle from './tableFilterToggle';
import { useSelector } from 'react-redux';
import { selectRowsPendingDeleteForComponent } from 'features/row-deletion/rowPendingDeletionSlice';
import { RootState } from 'app/store';
import { ro } from 'date-fns/locale';

interface Props<T extends RowData> {
  table: Table<T>;
  componentId: string;
  heightClass?: string;
  fontsizeClass?: string;
  cellClasses?: string;
  overflowClass?: string;
  renderCustomExpandedRow?: (rowId: string, row: T) => React.ReactNode;
}

// Come back to this when we come back to snags
const tryGetStickyOptions = (meta: any): string => {
  const stickyOptions: StickyOptions | undefined = meta?.stickyOptions;

  return stickyOptions?.isEnabled ? `sticky ${stickyOptions.side}-0 z-20` : '';
};

const isTableHeaderSticky = (meta: any): boolean => {
  const stickyOptions: StickyOptions | undefined = meta?.stickyOptions;
  return stickyOptions?.isEnabled ?? false;
};

export function CustomTable<T extends RowData>({
  table,
  componentId,
  heightClass,
  fontsizeClass,
  cellClasses,
  overflowClass,
  renderCustomExpandedRow
}: Props<T>) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const hasResized = useRef<boolean>(false);

  const getCustomHandlerResizer = (header: Header<T, unknown>) => {
    if (!hasResized.current) {
      hasResized.current = true;
    }
    return header.getResizeHandler();
  };

  const maxHeightClasses = `max-${heightClass}`;
  const fontsizeClasses = fontsizeClass ?? '';
  const overflowClasses = 'overflow-x-scroll';

  const tableContainerClasses = `${maxHeightClasses} ${fontsizeClasses} ${overflowClasses}`;

  const selectRowsPendingDelete = useSelector((state: RootState) =>
    selectRowsPendingDeleteForComponent(state, componentId)
  );

  const getBackgroundColour = useMemo(
    () =>
      (row: CoreRow<T>, index: number): string => {
        return selectRowsPendingDelete.some(
          (pendingRow) => pendingRow.rowId === row.id
        )
          ? 'bg-error-100'
          : index % 2 === 0
          ? 'bg-neutral-100'
          : 'bg-white';
      },
    [selectRowsPendingDelete]
  );

  return (
    <div
      key={componentId + '_custom-table'}
      ref={ref}
      className={tableContainerClasses}
    >
      <div className="">
        {table.getHeaderGroups().map((headerGroup) => {
          return (
            <Fragment key={headerGroup.id}>
              {headerGroup.headers.some((header) =>
                header.column.getCanFilter()
              ) && (
                <div
                  key={headerGroup.id + 'header_group_filter'}
                  className="flex flex-row flex-wrap mb-2 gap-2 items-center bg-white pt-4 pb-2"
                >
                  {headerGroup.headers
                    .filter((header) => header.column.getCanFilter())
                    .map((header, index, array) => {
                      return (
                        <Fragment key={header.id + '_header_filter'}>
                          <TableFilterToggle
                            column={header.column}
                            componentId={componentId}
                            table={table}
                            isLastColumn={index === array.length - 1}
                          />
                        </Fragment>
                      );
                    })}
                </div>
              )}
            </Fragment>
          );
        })}
        <table
          className="table-fixed p-0 m-0 border-t border-neutral-200"
          {...{
            style: {
              width:
                table.getCenterTotalSize() > width
                  ? table.getCenterTotalSize()
                  : width
            }
          }}
        >
          <thead className="first:text-left sticky overflow-hidden top-0 z-30 capitalize">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className="w-full" key={headerGroup.id + '_header_group'}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id + '_header'}
                    className={`first:text-left relative px-6 pl-2 p-2 py-3 w-fit bg-white text-gray font-normal text-sm whitespace-nowrap ${tryGetStickyOptions(
                      header.column.columnDef.meta
                    )}  ${cellClasses ?? ' px-6 p-2 py-4 '}`}
                    colSpan={header.colSpan}
                    style={{
                      width:
                        !header.column.getCanResize() ||
                        (!header.column.getIsResizing() &&
                          !hasResized.current &&
                          header.column.columnDef.size)
                          ? header.column.columnDef.size
                          : header.getSize()
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={`${
                            !header.isPlaceholder && header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : ''
                          } `}
                          onClick={
                            !header.isPlaceholder && header.column.getCanSort()
                              ? header.column.getToggleSortingHandler()
                              : () => {}
                          }
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <div className="absolute top-1/3 right-6 rounded select-none touch-none cursor-pointer">
                            {{
                              asc: <TableSortAscButton />,
                              desc: <TableSortDescButton />
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </div>
                        {header.column.getCanResize() &&
                        !isTableHeaderSticky(header.column.columnDef.meta) ? (
                          <span
                            onMouseDown={getCustomHandlerResizer(header)}
                            onTouchStart={getCustomHandlerResizer(header)}
                            className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none group"
                          >
                            <div
                              className={`absolute left-1/2 top-1/4 h-1/2 w-0.5 rounded select-none touch-none bg-off-navy group-hover:bg-navy cursor-col-resize ${
                                header.column.getIsResizing()
                                  ? 'isResizing'
                                  : ''
                              }`}
                            ></div>
                          </span>
                        ) : null}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={`${heightClass} overflow-y-auto relative`}>
            {table.getRowModel()?.rows?.map((row, index) => {
              const backgroundColour = getBackgroundColour(row, index);

              return (
                <Fragment key={row.id + '_row'}>
                  <tr className={backgroundColour}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id + '_cell'}
                        className={`text-left break-word align-text-top p-2 px-6 pl-2 ${backgroundColour} ${tryGetStickyOptions(
                          cell.column.columnDef.meta
                        )} ${cellClasses ?? 'truncate'}`}
                        style={{
                          width: cell.column.getSize()
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && renderCustomExpandedRow && (
                    <tr key={row.id + '_expanded_row'}>
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="p-0"
                      >
                        {renderCustomExpandedRow
                          ? renderCustomExpandedRow(row.id, row.original)
                          : null}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
